import React from 'react';
import { AlertCircle } from 'lucide-react';

export const computeLifecycle = (schedule) => {
  if (!schedule) {
    return { progress: 0, daysUntil: null, label: '', tone: 'neutral' };
  }

  const { lastServiceDate, intervalDays, nextDueDate } = schedule;

  let daysUntil = null;
  if (nextDueDate) {
    const target = new Date(nextDueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    daysUntil = Math.round((target - today) / 86400000);
  }

  let progress = 0;
  if (lastServiceDate && intervalDays) {
    const last = new Date(lastServiceDate);
    last.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const elapsedDays = Math.round((today - last) / 86400000);
    progress = Math.min(120, (elapsedDays / intervalDays) * 100);
    progress = Math.max(0, progress);
  } else if (daysUntil !== null && intervalDays) {
    const elapsed = intervalDays - daysUntil;
    progress = Math.min(120, Math.max(0, (elapsed / intervalDays) * 100));
  }

  let label = '';
  let tone = 'neutral';

  if (daysUntil === null) {
    label = 'Not tracked';
    tone = 'neutral';
  } else if (daysUntil < 0) {
    const overdueDays = Math.abs(daysUntil);
    label = `${overdueDays} day${overdueDays === 1 ? '' : 's'} overdue`;
    tone = 'overdue';
  } else if (daysUntil === 0) {
    label = 'Due today';
    tone = 'urgent';
  } else if (daysUntil <= 3) {
    label = `Due in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`;
    tone = 'urgent';
  } else if (daysUntil <= 7) {
    label = `Due in ${daysUntil} days`;
    tone = 'soon';
  } else if (daysUntil <= 14) {
    label = `In ${daysUntil} days`;
    tone = 'soon';
  } else if (daysUntil <= 30) {
    const weeks = Math.round(daysUntil / 7);
    label = `In ${weeks} week${weeks === 1 ? '' : 's'}`;
    tone = 'good';
  } else {
    const months = Math.round(daysUntil / 30);
    label = `In ${months} month${months === 1 ? '' : 's'}`;
    tone = 'good';
  }

  return { progress, daysUntil, label, tone };
};

const TONE_STYLES = {
  good: { bar: 'bg-[#A8D5BA]', text: 'text-[#5A9A70]' },
  soon: { bar: 'bg-[#EFA9B6]', text: 'text-[#EFA9B6]' },
  urgent: { bar: 'bg-[#E8B48E]', text: 'text-[#D4924E]' },
  overdue: { bar: 'bg-[#E88B8B]', text: 'text-[#E88B8B]' },
  neutral: { bar: 'bg-gray-300', text: 'text-gray-400' },
};

const MaintenanceLifecycle = ({ schedule, showLabel = true }) => {
  const { progress, label, tone, daysUntil } = computeLifecycle(schedule);
  const styles = TONE_STYLES[tone];

  if (daysUntil === null) return null;

  return (
    <div className="w-full">
      {showLabel && (
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs font-bold flex items-center gap-1.5 ${styles.text}`}>
            {tone === 'overdue' && <AlertCircle size={11} />}
            {label}
          </span>
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            Lifecycle
          </span>
        </div>
      )}

      <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ease-out ${styles.bar}`}
          style={{ width: `${Math.min(100, Math.max(2, progress))}%` }}
        />
      </div>
    </div>
  );
};

export default MaintenanceLifecycle;